import { useRef, useEffect } from 'react';

/**
 * Auto-scrolls to a bottom sentinel element.
 * Instant on mount, smooth on subsequent updates.
 *
 * @param {number} itemCount - Number of items (triggers scroll when it changes)
 * @param {string|number} [resetKey] - When this changes, reset to instant scroll
 * @returns {{ messagesEndRef: React.RefObject }}
 */
const useAutoScrollToBottom = (itemCount, resetKey) => {
  const messagesEndRef = useRef(null);
  const initialScrollDone = useRef(false);

  // Reset initial scroll flag when resetKey changes
  useEffect(() => {
    initialScrollDone.current = false;
  }, [resetKey]);

  // Auto-scroll to bottom: instant on mount/resetKey change, smooth on subsequent messages.
  // resetKey is in the dep array so the effect re-fires after the reset effect above flips
  // initialScrollDone, even when two tickets happen to have the same itemCount.
  useEffect(() => {
    if (!messagesEndRef.current || !itemCount) return;
    if (!initialScrollDone.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      initialScrollDone.current = true;
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [itemCount, resetKey]);

  return { messagesEndRef };
};

export default useAutoScrollToBottom;
